import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Settings } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useAuthStore } from '@/stores/auth-store';

export function UserAvatar() {
  const { t } = useTranslation(['auth', 'common']);
  const { user, signOut } = useAuthStore();

  if (!user) return null;

  const initials = user.displayName
    ? user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user.email?.[0]?.toUpperCase() ?? '?';

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      // Error handled in store
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: '2px solid var(--border-primary)',
            background: user.photoURL ? 'transparent' : 'linear-gradient(135deg, var(--accent-primary), var(--accent-primary-hover))',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            transition: 'border-color var(--motion-duration-fast) var(--motion-ease-standard), transform var(--motion-duration-fast) var(--motion-ease-standard)',
            padding: 0,
          }}
          aria-label={t('auth:a11y.userMenu')}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--accent-primary)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-primary)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {user.photoURL ? (
            <img
              src={user.photoURL}
              alt={user.displayName ?? ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              referrerPolicy="no-referrer"
            />
          ) : (
            <span style={{ color: 'white', fontSize: '13px', fontWeight: 700, lineHeight: 1 }}>
              {initials}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          style={{
            minWidth: '200px',
            background: 'white',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-primary)',
            boxShadow: 'var(--shadow-xl)',
            padding: '4px',
            zIndex: 50,
            animation: 'fadeInDown var(--motion-duration-fast) var(--motion-ease-standard)',
          }}
        >
          {/* User info */}
          <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid var(--border-secondary)', marginBottom: '4px' }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
              {user.displayName ?? t('auth:guest')}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
              {user.email}
            </div>
          </div>

          <DropdownMenu.Item asChild>
            <NavLink
              to="/settings"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                borderRadius: 'var(--radius-sm)',
                fontSize: '14px',
                color: 'var(--text-secondary)',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'background var(--motion-duration-micro) var(--motion-ease-standard)',
                outline: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--border-secondary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              onFocus={(e) => { e.currentTarget.style.background = 'var(--border-secondary)'; }}
              onBlur={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Settings style={{ width: '16px', height: '16px', color: 'var(--text-tertiary)' }} />
              {t('common:nav.settings')}
            </NavLink>
          </DropdownMenu.Item>

          <DropdownMenu.Item
            onSelect={handleSignOut}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 12px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '14px',
              color: 'var(--accent-error)',
              cursor: 'pointer',
              transition: 'background var(--motion-duration-micro) var(--motion-ease-standard)',
              outline: 'none',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--accent-error-bg)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            onFocus={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--accent-error-bg)'; }}
            onBlur={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <LogOut style={{ width: '16px', height: '16px' }} />
            {t('auth:signOut')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
